<script lang="ts">
  /**
   * Two-in-one image uploader used in admin material forms.
   *
   * mode='single' — single cover image. Picking a new file replaces the
   * existing one; "Прибрати" clears it back to null.
   *
   * mode='group' — gallery of up to `max` images (default 10). Adds to the
   * existing array; each tile has a remove button.
   *
   * The component never owns the source of truth — it reflects whatever the
   * parent passes via $bindable and calls onChange after each successful
   * upload/remove so the parent can re-validate / autosave.
   *
   * Uploads go through api.adminUploadImage which sends the raw file bytes
   * to /api/admin/images/upload — backend writes to Bunny Storage and returns
   * a public CDN URL.
   */
  import { api, ApiError } from '../lib/api';
  import ConfirmModal from './ConfirmModal.svelte';

  type Mode = 'single' | 'group';
  interface SingleProps {
    mode: 'single';
    value: string | null;
    onChange: (next: string | null) => void;
    max?: never;
    label?: string;
    /** Optional sizing hint for the loaded preview — mirrors the size the
     *  user will see on the material page. Affects only the preview tile,
     *  not the empty-state picker. */
    previewWidth?: 'full' | 'medium' | 'small';
    /** Fires when admin taps the loaded preview (not the × button). Used by
     *  BlockEditor to open the Lightbox at full screen. */
    onImageClick?: (url: string, index: number) => void;
    cols?: never;
  }
  interface GroupProps {
    mode: 'group';
    value: string[];
    onChange: (next: string[]) => void;
    max?: number;
    label?: string;
    previewWidth?: never;
    onImageClick?: (url: string, index: number) => void;
    /** Override the gallery tile grid — admin controls per-row count via
     *  this. Undefined falls back to the auto-fill default (96px min). */
    cols?: 2 | 3 | 4;
  }
  type Props = SingleProps | GroupProps;

  let { mode, value, onChange, max, label, previewWidth, onImageClick, cols }: Props = $props();

  // Destructuring a discriminated union via $props() loses the correlation
  // between `mode` and the shape of `value`/`onChange`: TypeScript collapses
  // the two variants into an intersection (`string & string[]`), so every call
  // fails to typecheck even though the runtime branch is right. These narrow
  // it back at the single point of use — the union stays intact for parents.
  const singleValue = $derived(mode === 'single' ? (value as string | null) : null);
  const groupValue = $derived(mode === 'group' ? (value as string[]) : []);
  const emitSingle = (next: string | null): void =>
    (onChange as (next: string | null) => void)(next);
  const emitGroup = (next: string[]): void => (onChange as (next: string[]) => void)(next);
  const MAX = $derived(max ?? 10);

  let uploading = $state(0);  // count of in-flight uploads
  let errorMsg = $state<string | null>(null);
  let fileInput = $state<HTMLInputElement | undefined>(undefined);

  const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
  const MAX_BYTES = 8 * 1024 * 1024; // backend enforces 8MB; matches here for early feedback

  async function uploadOne(file: File): Promise<string | null> {
    if (file.size > MAX_BYTES) {
      errorMsg = `Файл «${file.name}» більший за 8 МБ`;
      return null;
    }
    if (!ACCEPT.split(',').includes(file.type)) {
      errorMsg = `Формат ${file.type || 'невідомий'} не підтримується`;
      return null;
    }
    uploading += 1;
    try {
      const { url } = await api.adminUploadImage(file);
      return url;
    } catch (err) {
      if (err instanceof ApiError && err.status === 501) {
        errorMsg = 'Завантаження вимкнено (Bunny Storage не налаштовано в .env)';
      } else if (err instanceof ApiError && err.status === 415) {
        errorMsg = 'Формат не підтримується';
      } else if (err instanceof ApiError && err.status === 413) {
        errorMsg = 'Файл завеликий (макс 8 МБ)';
      } else {
        errorMsg = err instanceof Error ? err.message : 'Помилка завантаження';
      }
      return null;
    } finally {
      uploading -= 1;
    }
  }

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    errorMsg = null;

    if (mode === 'single') {
      const url = await uploadOne(files[0]);
      if (url) emitSingle(url);
      return;
    }

    // group mode — respect max slots, upload sequentially so onChange sees
    // images in the order the user picked them
    const current = groupValue;
    const slotsLeft = MAX - current.length;
    if (slotsLeft <= 0) {
      errorMsg = `Максимум ${MAX} картинок`;
      return;
    }
    const picked = Array.from(files).slice(0, slotsLeft);
    const accepted: string[] = [];
    for (const f of picked) {
      const url = await uploadOne(f);
      if (url) accepted.push(url);
    }
    if (accepted.length > 0) emitGroup([...current, ...accepted]);
  }

  function removeSingle(): void {
    emitSingle(null);
  }

  function removeAt(index: number): void {
    if (mode !== 'group') return;
    emitGroup(groupValue.filter((_, i) => i !== index));
  }

  // Two-step removal with a confirmation modal. `pendingRemove` is null when
  // closed, 'single' when removing the cover image, or a number = the index
  // in the gallery being removed.
  let pendingRemove = $state<null | 'single' | number>(null);
  function confirmRemove(): void {
    if (pendingRemove === 'single') removeSingle();
    else if (typeof pendingRemove === 'number') removeAt(pendingRemove);
    pendingRemove = null;
  }

  function openPicker(): void {
    if (uploading > 0) return;
    fileInput?.click();
  }

  // group counter for label
  const groupCount = $derived(groupValue.length);
  const groupFull = $derived(mode === 'group' && groupCount >= MAX);
</script>

<div class="uploader">
  {#if label}
    <div class="label-row">
      <span class="label">{label}</span>
      {#if mode === 'group'}
        <span class="counter" class:full={groupFull}>{groupCount} / {MAX}</span>
      {/if}
    </div>
  {/if}

  {#if mode === 'single'}
    {#if singleValue}
      <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
      <div
        class="single-preview width-{previewWidth ?? 'full'}"
        class:clickable={!!onImageClick}
        onclick={() => onImageClick?.(singleValue, 0)}
      >
        <img src={singleValue} alt="Cover preview" />
        <button
          type="button"
          class="remove"
          onclick={(e) => {
            e.stopPropagation();
            pendingRemove = 'single';
          }}
          disabled={uploading > 0}
        >
          ×
        </button>
      </div>
    {:else}
      <button type="button" class="picker single-empty" onclick={openPicker} disabled={uploading > 0}>
        {#if uploading > 0}
          <span class="spinner"></span>
          <span>Завантаження...</span>
        {:else}
          <span class="plus">+</span>
          <span>Додати картинку</span>
        {/if}
      </button>
    {/if}
  {:else}
    <div class="group{cols ? ` cols-${cols}` : ''}">
      {#each groupValue as url, i (url + i)}
        <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
        <div
          class="group-tile"
          class:clickable={!!onImageClick}
          onclick={() => onImageClick?.(url, i)}
        >
          <img src={url} alt={`Image ${i + 1}`} />
          <button
            type="button"
            class="remove"
            onclick={(e) => {
              e.stopPropagation();
              pendingRemove = i;
            }}
            disabled={uploading > 0}
          >
            ×
          </button>
        </div>
      {/each}
      {#if !groupFull}
        <button
          type="button"
          class="picker group-empty"
          onclick={openPicker}
          disabled={uploading > 0}
        >
          {#if uploading > 0}
            <span class="spinner"></span>
          {:else}
            <span class="plus">+</span>
          {/if}
        </button>
      {/if}
    </div>
  {/if}

  <input
    bind:this={fileInput}
    type="file"
    accept={ACCEPT}
    multiple={mode === 'group'}
    onchange={(e) => {
      const input = e.currentTarget as HTMLInputElement;
      handleFiles(input.files);
      input.value = '';  // allow re-picking the same file later
    }}
    hidden
  />

  {#if errorMsg}
    <div class="error">{errorMsg}</div>
  {/if}
</div>

<ConfirmModal
  open={pendingRemove !== null}
  title="Видалити картинку?"
  message="Картинка одразу видалиться з Bunny — повернути її не можна."
  confirmLabel="Так, видалити"
  cancelLabel="Скасувати"
  onConfirm={confirmRemove}
  onCancel={() => (pendingRemove = null)}
/>

<style>
  .uploader {
    margin-bottom: 14px;
  }
  .label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
  }
  .counter {
    font-size: 11px;
    color: var(--text-muted);
  }
  .counter.full {
    color: var(--accent-text);
  }

  /* Single image */
  /* Loaded preview shows the image at its native aspect ratio so admin sees
   * exactly what users will see (no 16:9 crop). The empty-state picker below
   * keeps a 16:9 placeholder since there's no image yet to dictate the shape.
   * .width-* classes mirror the size selector — preview shrinks to match what
   * the user will see, centered horizontally for medium/small. */
  .single-preview {
    position: relative;
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
    background: var(--bg2);
    border: 1px solid var(--border);
    transition: width 0.2s ease;
  }
  .single-preview.width-medium {
    width: 70%;
    margin-left: auto;
    margin-right: auto;
  }
  .single-preview.width-small {
    width: 45%;
    margin-left: auto;
    margin-right: auto;
  }
  .single-preview img {
    width: 100%;
    height: auto;
    display: block;
  }
  /* The clickable wrapper covers the whole preview surface — gives the admin
   * a reliable target even when svelte-dnd-action is also listening for drag
   * gestures inside the same block. The × button stops propagation so its
   * own onclick still wins. */
  .single-preview.clickable,
  .group-tile.clickable {
    cursor: zoom-in;
  }

  .single-empty {
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  /* Group — default auto-fill thumbnails; cols-N classes pin a fixed
   * column count so the editor mirrors what the user will see. */
  .group {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
  }
  .group.cols-2 { grid-template-columns: repeat(2, 1fr); }
  .group.cols-3 { grid-template-columns: repeat(3, 1fr); }
  .group.cols-4 { grid-template-columns: repeat(4, 1fr); }
  .group-tile {
    position: relative;
    aspect-ratio: 1;
    border-radius: 10px;
    overflow: hidden;
    background: var(--bg2);
    border: 1px solid var(--border);
  }
  .group-tile img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .group-empty {
    aspect-ratio: 1;
  }

  /* Picker button */
  .picker {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: var(--bg2);
    border: 1.5px dashed var(--border2);
    color: var(--text-muted);
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
    font-size: 12px;
    font-family: inherit;
    padding: 12px;
  }
  .picker:hover:not(:disabled) {
    border-color: var(--outline);
    color: var(--accent-text);
    background: var(--pu-light);
  }
  .picker:disabled {
    opacity: 0.5;
    cursor: progress;
  }
  .plus {
    font-size: 24px;
    line-height: 1;
    font-weight: 300;
  }

  /* Remove ×  button */
  .remove {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.6);
    color: var(--text-light);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-family: inherit;
    transition: background 0.15s;
  }
  .remove:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.85);
  }
  .remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Spinner reused in disabled picker */
  .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(168, 80, 47, 0.3);
    border-top-color: var(--pu);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error {
    margin-top: 8px;
    font-size: 12px;
    color: var(--heart);
    line-height: 1.4;
  }
</style>
